import { useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  FormHelperText,
  Input,
  Textarea,
  VStack,
  HStack,
  Text,
  Grid,
  Spinner,
  Switch,
  InputGroup,
  InputRightElement,
  Tag,
  TagLabel,
  TagCloseButton,
  Flex,
  Wrap,
  Select,
} from '@chakra-ui/react'
import { Plus } from 'lucide-react'
import { apiClient } from '@/lib/api/client'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { TreeDropdown, type TreeDropdownRef } from '@/components/forms/TreeDropdown'
import {
  createContainerYardSchema,
  updateContainerYardSchema,
  type CreateContainerYardFormData,
  type UpdateContainerYardFormData,
} from '@/lib/validations/auth'

interface ContainerYardFormProps {
  containerYardId?: number
  onSuccess?: () => void
  onCancel?: () => void
}

type ContainerYardFormValues = CreateContainerYardFormData | UpdateContainerYardFormData

export function ContainerYardForm({ containerYardId, onSuccess, onCancel }: ContainerYardFormProps) {
  const navigate = useNavigate()
  const { showError, showSuccess } = useToastNotification()
  const containerYardRef = useRef<TreeDropdownRef>(null)

  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [landlineInput, setLandlineInput] = useState('')
  const landlineInputRef = useRef<HTMLInputElement>(null)
  const isEditMode = !!containerYardId

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
  } = useForm<ContainerYardFormValues>({
    resolver: zodResolver(isEditMode ? updateContainerYardSchema : createContainerYardSchema),
    defaultValues: {
      short_name: '',
      parent_id: null,
      is_active: 1,
      landlines: '' as any, // Type assertion needed because zod infers output type (array) but form needs input type (string)
    },
  })

  useEffect(() => {
    if (isEditMode && containerYardId) {
      setIsLoading(true)
      apiClient
        .get(`/container-yards/get-yard-by-id/${containerYardId}`)
        .then(({ data }) => {
          const containerYardData = data?.data || data || {}

          let parentId: number | null = null
          if (containerYardData.parent_id) {
            parentId = containerYardData.parent_id
          } else if (containerYardData.parent_containerYard?.id) {
            parentId = containerYardData.parent_containerYard.id
          }

          // Convert landlines array to comma-separated string if it's an array
          const landlinesValue = Array.isArray(containerYardData.landlines)
            ? containerYardData.landlines.join(', ')
            : (containerYardData.landlines || '')

          reset({
            name: containerYardData.name || '',
            short_name: containerYardData.short_name || '',
            address: containerYardData.address || '',
            contact_name: containerYardData.contact_name || '',
            contact_mobile: containerYardData.contact_mobile || '',
            landlines: landlinesValue as any, // Type assertion needed because zod infers output type (array) but form needs input type (string)
            location_type: containerYardData.location_type || '',
            is_active: containerYardData.is_active ?? 1,
            parent_id: parentId,
          })
        })
        .catch((error: any) => {
          showError(error)
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }, [containerYardId, isEditMode, reset, showError])

  const onSubmit = async (data: ContainerYardFormValues) => {
    setIsSubmitting(true)

    try {
      const parentValue = containerYardRef.current?.getValue()
      const payload = {
        name: data.name.trim(),
        short_name: data.short_name.trim(),
        address: data.address.trim(),
        contact_name: data.contact_name,
        contact_mobile: data.contact_mobile,
        landlines: data.landlines,
        location_type: data.location_type,
        is_active: data.is_active,
        parent_id: parentValue?.id || null,
      }

      if (isEditMode) {
        await apiClient.put(`/container-yards/update-yard/${containerYardId}`, payload)
        showSuccess('Success', 'Container Yard updated successfully')
      } else {
        await apiClient.post('/container-yards/add-yard', payload)
        showSuccess('Success', 'Container Yard created successfully')
      }

      if (onSuccess) {
        onSuccess()
      } else {
        navigate('/container-yards')
      }
    } catch (error: any) {
      showError(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    if (onCancel) {
      onCancel()
    } else {
      navigate('/container-yards')
    }
  }

  if (isLoading) {
    return (
      <VStack spacing={4} py={8}>
        <Spinner size="xl" color="blue.500" />
        <Text>Loading Container Yard...</Text>
      </VStack>
    )
  }

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit as any)}>
      <VStack spacing={4} align="stretch">
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Name <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.name}>
            <Input {...register('name')} placeholder="e.g. ABC Container Yard" />
            <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
            <FormHelperText>The name of the container yard.</FormHelperText>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Short Name <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.short_name}>
            <Input {...register('short_name')} placeholder="e.g. ABC CY" />
            <FormErrorMessage>{errors.short_name?.message}</FormErrorMessage>
            <FormHelperText>The short name or abbreviation for this container yard.</FormHelperText>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Location <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.address}>
            <Textarea
              {...register('address')}
              placeholder="e.g. 123 ABC St. Brgy. ABC, Quezon City"
              rows={4}
            />
            <FormErrorMessage>{errors.address?.message}</FormErrorMessage>
            <FormHelperText>The container yard's address.</FormHelperText>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Contact Person
          </FormLabel>
          <FormControl isInvalid={!!errors.contact_name}>
            <Input {...register('contact_name')} placeholder="e.g. Jane Doe" />
            <FormErrorMessage>{errors.contact_name?.message}</FormErrorMessage>
            <FormHelperText>The contact person for this container yard.</FormHelperText>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Mobile Number
          </FormLabel>
          <FormControl isInvalid={!!errors.contact_mobile}>
            <Input
              {...register('contact_mobile', {
                onChange: (e) => {
                  e.target.value = e.target.value.replace(/[^0-9\s+]/g, '')
                },
              })}
              type="tel"
              placeholder="e.g. 09123456789 or +63 912 345 6789"
              pattern="[0-9\s+]*"
            />
            <FormErrorMessage>{errors.contact_mobile?.message}</FormErrorMessage>
            <FormHelperText>The contact number (mobile) for this container yard.</FormHelperText>
          </FormControl>
        </Grid>

         <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Landline Number/s
          </FormLabel>
          <FormControl isInvalid={!!errors.landlines}>
            <Controller
              name="landlines"
              control={control}
              render={({ field }) => {
                // Ensure field.value is always a string (handle both string and array from API)
                const fieldValueString = Array.isArray(field.value)
                  ? field.value.join(', ')
                  : (field.value || '')
                
                // Convert comma-separated string to array for display
                const landlineNumbers = fieldValueString
                  ? fieldValueString.split(',').map((item: string) => item.trim()).filter((item: string) => item.length > 0)
                  : []

                const handleAddLandline = () => {
                  const trimmedValue = landlineInput.trim()
                  if (trimmedValue && !landlineNumbers.includes(trimmedValue)) {
                    const updatedNumbers = [...landlineNumbers, trimmedValue]
                    field.onChange(updatedNumbers.join(', '))
                    setLandlineInput('')
                    landlineInputRef.current?.focus()
                  }
                }

                const handleRemoveLandline = (numberToRemove: string) => {
                  const updatedNumbers = landlineNumbers.filter((num: string) => num !== numberToRemove)
                  field.onChange(updatedNumbers.length > 0 ? updatedNumbers.join(', ') : '')
                }

                const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleAddLandline()
                  }
                }

                return (
                  <VStack spacing={2} align="stretch">
                    <InputGroup>
                      <Input
                        ref={landlineInputRef}
                        value={landlineInput}
                        onChange={(e) => setLandlineInput((e.target.value).replace(/[^0-9\s+]/g, ''))}
                        onKeyDown={handleKeyDown}
                        placeholder="e.g. +63 2 8123 4567 or 281234567 or 02 8123 4567"
                        pr="5.5rem"
                        pattern="[0-9\s+]*"
                        type="tel"
                      />
                      <InputRightElement width="5rem" pr={1}>
                        <Button
                          h="1.75rem"
                          size="sm"
                          onClick={handleAddLandline}
                          isDisabled={!landlineInput.trim()}
                          leftIcon={<Plus size={14} />}
                        >
                          Add
                        </Button>
                      </InputRightElement>
                    </InputGroup>
                    {landlineNumbers.length > 0 && (
                      <Wrap spacing={2} mt={2}>
                        {landlineNumbers.map((number: string, index: number) => (
                          <Tag key={index} size="md" colorScheme="blue" borderRadius="full">
                            <TagLabel>{number}</TagLabel>
                            <TagCloseButton onClick={() => handleRemoveLandline(number)} />
                          </Tag>
                        ))}
                      </Wrap>
                    )}
                  </VStack>
                )
              }}
            />
            <FormErrorMessage>{errors.landlines?.message}</FormErrorMessage>
            <FormHelperText>The landline number/s for this container yard. (Input the landline number and press Enter in the keyboard or click the +Add button.)</FormHelperText>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Location Type <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.location_type}>
            <Select
              {...register('location_type')}
              placeholder="Select location type"
            >
              <option value="Container Yard">Container Yard</option>
              <option value="Port Area">Port Area</option>
            </Select>
            <FormErrorMessage>{errors.location_type?.message}</FormErrorMessage>
            <FormHelperText>The location type for this container yard.</FormHelperText>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="center">
          <FormLabel pt={2} fontWeight="medium">
            Status <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.is_active}>
            <Controller
              name="is_active"
              control={control}
              render={({ field }) => (
                <Switch
                  isChecked={field.value === 1}
                  onChange={(e) => {
                    field.onChange(e.target.checked ? 1 : 0)
                  }}
                  colorScheme="blue"
                />
              )}
            />
            <FormErrorMessage>{errors.is_active?.message}</FormErrorMessage>
            <FormHelperText>Toggle to activate or deactivate this container yard.</FormHelperText>
          </FormControl>
        </Grid>

        <HStack spacing={4} justify="flex-end" pt={4}>
          <Button variant="outline" onClick={handleCancel} isDisabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            colorScheme="blue"
            isLoading={isSubmitting}
            loadingText={isEditMode ? 'Saving...' : 'Creating...'}
          >
            {isEditMode ? 'Save' : 'Create Container Yard'}
            {isSubmitting && (
              <Text as="span" ml={2} fontSize="sm">
                ...
              </Text>
            )}
          </Button>
        </HStack>
      </VStack>
    </Box>
  )
}
